export const ARTICLE_MAX_LENGTH = 100
export const SUPPLIER_ARTICLES_MAX_COUNT = 20
export const SUPPLIER_ARTICLE_NORMALIZED_MAX_LENGTH = 30

interface ArticleFields {
  article?: string | null
  articleOriginal?: string | null
}

interface SupplierArticleFields {
  supplierArticles?: string[] | null
  supplierArticlesOriginal?: string[] | null
}

export const normalizeArticleKey = (value: string) =>
  value.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase()

export const getDisplayArticle = ({
  article,
  articleOriginal,
}: ArticleFields) => articleOriginal || article || ''

export const getDisplaySupplierArticles = ({
  supplierArticles,
  supplierArticlesOriginal,
}: SupplierArticleFields) =>
  supplierArticlesOriginal?.length
    ? supplierArticlesOriginal
    : (supplierArticles ?? [])

export const cleanSupplierArticles = (values: string[]) =>
  values.map((value) => value.trim()).filter((value) => value !== '')

export const findDuplicateSupplierArticles = (values: string[]) => {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const value of values) {
    const key = normalizeArticleKey(value)
    if (seen.has(key)) duplicates.add(key)
    seen.add(key)
  }
  return values.filter((value) => duplicates.has(normalizeArticleKey(value)))
}

export const getSupplierArticlesError = (values: string[]) => {
  const cleaned = cleanSupplierArticles(values)
  if (cleaned.length > SUPPLIER_ARTICLES_MAX_COUNT)
    return `Не более ${SUPPLIER_ARTICLES_MAX_COUNT} артикулов поставщика`
  if (cleaned.some((value) => value.length > ARTICLE_MAX_LENGTH))
    return `Артикул поставщика не длиннее ${ARTICLE_MAX_LENGTH} символов`
  if (
    cleaned.some(
      (value) =>
        normalizeArticleKey(value).length >
        SUPPLIER_ARTICLE_NORMALIZED_MAX_LENGTH
    )
  )
    return `Артикул поставщика без пробелов и знаков препинания не длиннее ${SUPPLIER_ARTICLE_NORMALIZED_MAX_LENGTH} символов`
  return null
}

export const toNormalizedArticle = (value: string) =>
  normalizeArticleKey(value).toUpperCase()

const uniqueByArticleKey = (values: string[]) => {
  const seen = new Set<string>()
  return values.filter((value) => {
    const key = normalizeArticleKey(value)
    if (key === '' || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export type SupplierArticlesAction = 'SET' | 'ADD' | 'REMOVE' | 'CLEAR'

export const applySupplierArticlesAction = (
  current: string[],
  action: SupplierArticlesAction,
  values: string[]
) => {
  const cleaned = cleanSupplierArticles(values)
  switch (action) {
    case 'SET':
      return uniqueByArticleKey(cleaned)
    case 'ADD':
      return uniqueByArticleKey([...current, ...cleaned])
    case 'REMOVE': {
      const removed = new Set(cleaned.map(normalizeArticleKey))
      return current.filter((value) => !removed.has(normalizeArticleKey(value)))
    }
    case 'CLEAR':
      return []
  }
}

export const collectSupplierArticles = (lists: string[][]) =>
  uniqueByArticleKey(lists.flat())
